/**
 * CQL2-Text filter expression builder for OGC API Features / Tiles.
 *
 * Implements Common Query Language (CQL2) as defined in OGC API – Features Part 3: Filtering.
 * Produces CQL2-Text strings that are passed as `filter=` query parameters along with
 * `filter-lang=cql2-text` to OGC endpoints.
 *
 * Reference: https://docs.ogc.org/DRAFTS/21-065r2.html
 */

// ─── Escape helpers ──────────────────────────────────────────────────────────

/** Escape a CQL2 string literal (single-quote delimited, '' escapes a literal ') */
export function escapeString(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/** Format a number for CQL2 (no quoting) */
function num(value: number): string {
  return String(value);
}

/** Format a date/datetime for CQL2 — TIMESTAMP('...') or DATE('...') */
export function timestamp(iso: string): string {
  return `TIMESTAMP('${iso}')`;
}

export function date(iso: string): string {
  return `DATE('${iso}')`;
}

// ─── Comparison operators ────────────────────────────────────────────────────

export function eq(property: string, value: string | number | boolean): string {
  if (typeof value === 'string') return `${property} = ${escapeString(value)}`;
  return `${property} = ${value}`;
}

export function neq(property: string, value: string | number | boolean): string {
  if (typeof value === 'string') return `${property} <> ${escapeString(value)}`;
  return `${property} <> ${value}`;
}

export function gt(property: string, value: number): string {
  return `${property} > ${num(value)}`;
}

export function gte(property: string, value: number): string {
  return `${property} >= ${num(value)}`;
}

export function lt(property: string, value: number): string {
  return `${property} < ${num(value)}`;
}

export function lte(property: string, value: number): string {
  return `${property} <= ${num(value)}`;
}

export function between(property: string, low: number, high: number): string {
  return `${property} BETWEEN ${num(low)} AND ${num(high)}`;
}

export function isNull(property: string): string {
  return `${property} IS NULL`;
}

export function isNotNull(property: string): string {
  return `${property} IS NOT NULL`;
}

// ─── Pattern matching ────────────────────────────────────────────────────────

/** Case-sensitive LIKE: % = any chars, _ = single char */
export function like(property: string, pattern: string): string {
  return `${property} LIKE ${escapeString(pattern)}`;
}

/** Case-insensitive ILIKE */
export function ilike(property: string, pattern: string): string {
  return `${property} ILIKE ${escapeString(pattern)}`;
}

/** Convenience: case-insensitive contains */
export function contains(property: string, substring: string): string {
  return ilike(property, `%${substring}%`);
}

/** Convenience: case-insensitive starts with */
export function startsWith(property: string, prefix: string): string {
  return ilike(property, `${prefix}%`);
}

// ─── Set membership ──────────────────────────────────────────────────────────

export function inList(property: string, values: (string | number)[]): string {
  const formatted = values.map((v) => typeof v === 'string' ? escapeString(v) : num(v)).join(', ');
  return `${property} IN (${formatted})`;
}

// ─── Logical combinators ─────────────────────────────────────────────────────

export function and(...clauses: (string | null | undefined | false)[]): string {
  const valid = clauses.filter(Boolean) as string[];
  if (valid.length === 0) return '';
  if (valid.length === 1) return valid[0];
  return valid.map((c) => `(${c})`).join(' AND ');
}

export function or(...clauses: (string | null | undefined | false)[]): string {
  const valid = clauses.filter(Boolean) as string[];
  if (valid.length === 0) return '';
  if (valid.length === 1) return valid[0];
  return valid.map((c) => `(${c})`).join(' OR ');
}

export function not(clause: string): string {
  return `NOT (${clause})`;
}

// ─── Spatial functions (CQL2 spatial operators) ──────────────────────────────

/** S_INTERSECTS — features whose geometry intersects a given geometry */
export function sIntersects(property: string, wkt: string): string {
  return `S_INTERSECTS(${property}, ${wkt})`;
}

/** S_WITHIN — features whose geometry is within a given geometry */
export function sWithin(property: string, wkt: string): string {
  return `S_WITHIN(${property}, ${wkt})`;
}

/** S_CONTAINS — features whose geometry contains a given geometry */
export function sContains(property: string, wkt: string): string {
  return `S_CONTAINS(${property}, ${wkt})`;
}

/** S_CROSSES — features whose geometry crosses a given geometry */
export function sCrosses(property: string, wkt: string): string {
  return `S_CROSSES(${property}, ${wkt})`;
}

/** S_DISJOINT — features whose geometry is disjoint from a given geometry */
export function sDisjoint(property: string, wkt: string): string {
  return `S_DISJOINT(${property}, ${wkt})`;
}

/** S_EQUALS — features whose geometry equals a given geometry */
export function sEquals(property: string, wkt: string): string {
  return `S_EQUALS(${property}, ${wkt})`;
}

/** S_TOUCHES — features whose geometry touches a given geometry */
export function sTouches(property: string, wkt: string): string {
  return `S_TOUCHES(${property}, ${wkt})`;
}

/** S_OVERLAPS — features whose geometry overlaps a given geometry */
export function sOverlaps(property: string, wkt: string): string {
  return `S_OVERLAPS(${property}, ${wkt})`;
}
